import { motion } from 'framer-motion';
import { css, styled } from 'styled-components';

import { accordionTransition, AccordionVariant, accordionVertical } from '@pkg/motion';
import {
    CommonAction,
    floorGameDuration as duration,
    floorGameEasing as easing,
    recipesCommonAction,
    styledLayout,
    styledMedia,
    styledTypography,
} from '@pkg/primitives';

import { tokens } from './FilterGroup.tokens.ts';

interface GroupProps {
    $expanded?: boolean;
}

export const Group = styled.div<GroupProps>`
    --filter-group-bg: rgb(255, 255, 255, 0.04);

    // Requires 'grid' layout for scrollable Content.
    display: grid;
    border-radius: ${tokens.group.radius}px;
    background-color: var(--filter-group-bg);
    transition: background-color ${duration.normal}ms ${easing.cubic};

    ${({ $expanded }) =>
        $expanded &&
        css`
            --filter-group-bg: rgb(255, 255, 255, 0.06);
        `};
`;

export const Header = styled.header`
    display: grid;
    grid-auto-flow: column;
    align-items: center;
    justify-content: space-between;
`;

export const LabelWrapper = styled.div`
    display: grid;
    grid-auto-flow: column;
    align-items: center;
    gap: ${tokens.label.gap}px;
    padding: 0 ${tokens.label.padding}px;
`;

export const Label = styled.p`
    ${styledTypography.allianceBase};
    ${styledTypography.truncate};
    ${styledTypography.truncateHeightFix()};
    font-size: ${tokens.label.size}px;
    font-weight: 700;
    text-transform: uppercase;
`;

export const Badge = styled.div`
    display: grid;
    place-items: center;
    place-content: center;
    min-width: ${tokens.badge.size}px;
    height: ${tokens.badge.size}px;
    border-radius: 999px;
    background-color: rgb(255, 255, 255, 0.1);
`;

export const Count = styled.p`
    ${styledTypography.allianceBase};
    ${styledTypography.textBoxTrim};
    padding: 0 ${tokens.count.padding}px;
    font-size: ${tokens.count.size}px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    letter-spacing: -0.02em;
    color: rgb(255, 255, 255, 0.6);
`;

export const ToggleAction = styled(CommonAction)`
    ${recipesCommonAction.basic};
    --toggle-action-bg: rgb(255, 255, 255, 0.1);

    width: ${tokens.toggleAction.size}px;
    height: ${tokens.toggleAction.size}px;
    border-radius: ${tokens.group.radius}px;
    // Since most of the button is an invisible hitbox,
    // inset the focus-ring.
    outline-offset: -2px;

    &[aria-pressed] {
    }

    &:disabled {
        opacity: 0.8;
    }

    &:hover:not(:active) {
        --toggle-action-bg: rgb(255, 255, 255, 0.16);
    }
`;

export const Bubble = styled.div`
    display: grid;
    place-items: center;
    place-content: center;
    width: ${tokens.toggleAction.bubbleSize}px;
    height: ${tokens.toggleAction.bubbleSize}px;
    border-radius: 999px;
    background-color: var(--toggle-action-bg);
    transition: background-color ${duration.normal}ms ${easing.cubic};
`;

export const IconWrapper = styled.div`
    ${styledMedia.iconWrapper};
    width: ${tokens.toggleAction.iconSize}px;
    height: ${tokens.toggleAction.iconSize}px;
`;

interface ContentProps {
    $scrollable?: boolean;
}

export const Content = styled(motion.div).attrs({
    variants: accordionVertical,
    initial: AccordionVariant.Hidden,
    animate: AccordionVariant.Show,
    exit: AccordionVariant.Hidden,
    transition: accordionTransition,
})<ContentProps>`
    ${({ $scrollable }) => {
        if ($scrollable) {
            return css`
                max-height: ${tokens.content.scrollHeight}px;
            `;
        } else {
            return css`
                overflow: hidden;
            `;
        }
    }};
`;

export const ContentInterior = styled.div<ContentProps>`
    padding: ${tokens.content.padding}px;

    ${({ $scrollable }) =>
        $scrollable &&
        css`
            --yat_scrollbar-thumb-color: ${({ theme }) => theme.shades.purple['20']};
            height: 100%;
            overflow-y: scroll;

            ${styledLayout.scrollbar({
                thumbColor: 'var(--yat_scrollbar-thumb-color)',
                trackColor: 'transparent',
            })};
        `};
`;
